import {
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
  computed,
  ChangeDetectionStrategy,
  DestroyRef,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, filter, skip } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';


import { FormsModule } from '@angular/forms';
import { DataDisplayerModule } from 'src/app/components/data-displayer/data-displayer.module';
import { SelectComponent } from 'src/app/shared/components/select/select.component';
import { SharedModule } from 'src/app/shared/shared.module';
import { DateRangeComponent } from 'src/app/shared/date-range/date-range.component';
import { AppLinkComponent } from 'src/app/shared/app-link/app-link.component';
import { EllipsisLoadingComponent } from 'src/app/shared/components/ellipsis-loading/ellipsis-loading.component';
import { ConfirmationDialogComponent } from 'src/app/shared/components/modals/confirmation-dialog/confirmation-dialog.component';

import { WalletResponseDto } from 'src/generated-sources/openapi/wallet-service/wallet';
import { GlobalServiceService } from 'src/app/services/global/global-service.service';
import { WalletTrendFacade, WalletTrendIndicatorDto } from './wallet-trend.facade';
import { ChartConfigService } from 'src/app/views/attribute-evolution/chart-config.service';

@Component({
  selector: 'app-wallet-trend',
  templateUrl: './wallet-trend.component.html',
  styleUrls: ['./wallet-trend.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    DataDisplayerModule,
    SelectComponent,
    SharedModule,
    AppLinkComponent,
    DateRangeComponent,
    EllipsisLoadingComponent
],
  providers: [WalletTrendFacade],
})
export class WalletTrendComponent implements OnInit {
  readonly unavailableText = $localize`:@@js_unavailable:indisponible`;
  readonly loadingIndicatorsText = $localize`:@@loadingIndicators:Cargando indicadores...`;
  readonly maxDate = new Date();

  readonly wallet = input.required<WalletResponseDto>();
  readonly backToList = output<void>();

  protected readonly facade = inject(WalletTrendFacade);
  private readonly chartConfigService = inject(ChartConfigService);
  private readonly globalService = inject(GlobalServiceService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private readonly attributeInfoContent = viewChild<TemplateRef<any>>('attributeInfoContent');

  protected readonly startDate = signal<Date | null>(null);
  protected readonly endDate = signal<Date | null>(null);

  protected readonly walletCreationDate = computed(() => {
    const createdAt = this.wallet()?.control?.createdAt;
    if (!createdAt) return null;
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), 1);
  });

  protected readonly isLoading = computed(
    () => this.facade.isLoadingIndicators() || this.facade.isLoadingChart(),
  );

  protected readonly canLoadData = computed(
    () => !!(this.facade.selectedIndicator() && this.startDate() && this.endDate()),
  );

  protected readonly chartOptions = computed(() => {
    const chartData = this.facade.chartData();
    const selectedIndicator = this.facade.selectedIndicator();
    const numericValues = (chartData.data.values ?? []).filter((v): v is number => v !== null);
    if (numericValues.length === 0) return null;

    const baseOptions = this.chartConfigService.generateChartOptions(
      numericValues,
      selectedIndicator?.displayLabel,
    );

    return {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        title: { display: false },
        legend: { display: true, position: 'bottom' as const },
      },
      elements: {
        point: {
          radius: 4.5,
          hoverRadius: 7,
          hitRadius: 12,
          backgroundColor: '#42A5F5',
          borderColor: '#ffffff',
          borderWidth: 2,
        },
        line: { tension: 0.1, borderWidth: 4, borderColor: '#42A5F5' },
      },
    };
  });

  readonly optionLabelFn = (indicator: WalletTrendIndicatorDto) => indicator?.displayLabel ?? '';

  constructor() {
    this.setupReactiveDataLoading();
  }

  ngOnInit(): void {
    this.facade.loadIndicators(this.wallet().planId);

    this.globalService
      .getPersonType$()
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onBackClick());
  }

  protected onIndicatorSelect(selection: WalletTrendIndicatorDto): void {
    this.facade.setSelectedIndicator(selection);
  }

  protected onStartDateChange(date: Date): void {
    if (date) this.startDate.set(date);
  }

  protected onEndDateChange(date: Date): void {
    if (date) this.endDate.set(date);
  }

  protected onBackClick(): void {
    this.backToList.emit();
  }

  protected openAttributeInfoModal(): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: $localize`:@@walletIndiceModalTitle:¿Qué es el índice de cartera?`,
        contentTemplate: this.attributeInfoContent(),
        cancelButton: { hide: true },
      },
      width: '400px',
    });
  }

  private setupReactiveDataLoading(): void {
    toObservable(
      computed(() => ({
        indicator: this.facade.selectedIndicator(),
        start: this.startDate(),
        end: this.endDate(),
        isLoadingChart: this.facade.isLoadingChart(),
        isLoadingIndicators: this.facade.isLoadingIndicators(),
      })),
    )
      .pipe(
        debounceTime(200),
        filter(
          (
            args,
          ): args is {
            indicator: WalletTrendIndicatorDto;
            start: Date;
            end: Date;
            isLoadingChart: boolean;
            isLoadingIndicators: boolean;
          } =>
            !!(
              args.indicator &&
              args.start &&
              args.end &&
              !args.isLoadingChart &&
              !args.isLoadingIndicators
            ),
        ),
        distinctUntilChanged(
          (prev, curr) =>
            prev.indicator?.id === curr.indicator?.id &&
            prev.start?.getTime() === curr.start?.getTime() &&
            prev.end?.getTime() === curr.end?.getTime(),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ indicator, start, end }) => {
        this.facade.loadChartData(this.wallet().id, indicator.id, start, end);
      });
  }
}
